// src/components/Onboarding.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../App";
import { createUserProfile } from "../services/firebase";
import { calculateTDEE } from "../utils/tdeeCalculator";
import { calculateMacros } from "../utils/macroCalculator";
import { estimateBodyFat } from "../utils/bodyFatEstimator";

const TOTAL_STEPS = 5;

// ─── Step progress indicator ──────────────────────────────────────────────────
const StepIndicator = ({ current, total }) => (
  <div className="flex items-center gap-2 mb-8">
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        className={`h-1 flex-1 rounded-full transition-all duration-300 ${
          i < current ? "bg-[#ff7c0a]" : i === current ? "bg-[#ff7c0a]/50" : "bg-[#1f2a3c]"
        }`}
      />
    ))}
  </div>
);

// ─── Section header ───────────────────────────────────────────────────────────
const StepHeader = ({ icon, title, subtitle }) => (
  <div className="mb-6">
    <div className="text-3xl mb-2">{icon}</div>
    <h2 className="section-title text-xl">{title}</h2>
    {subtitle && <p className="text-slate-400 text-sm mt-1">{subtitle}</p>}
  </div>
);

// ─── Activity level options ───────────────────────────────────────────────────
const ACTIVITY_OPTIONS = [
  { value: "sedentary",   label: "Sedentary",    desc: "Little/no exercise, desk job",       mult: 1.2   },
  { value: "light",       label: "Light",         desc: "Light exercise 1–3 days/week",       mult: 1.375 },
  { value: "moderate",    label: "Moderate",      desc: "Moderate exercise 3–5 days/week",    mult: 1.55  },
  { value: "active",      label: "Active",        desc: "Hard exercise 6–7 days/week",        mult: 1.725 },
  { value: "very_active", label: "Very Active",   desc: "Very hard exercise + physical job",  mult: 1.9   },
];

// ─── Main component ───────────────────────────────────────────────────────────
export default function Onboarding() {
  const { user, refreshProfile } = useApp();
  const navigate = useNavigate();
  const [step,    setStep]    = useState(0);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  // Form state
  const [form, setForm] = useState({
    name:           "",
    sex:            "male",
    age:            "",
    height_ft:      "",
    height_in:      "",
    weight_start:   "",
    goal_mode:      "weight", // "weight" | "percent"
    weight_goal:    "",
    goal_pct:       "8",
    activity_level: "moderate",
    deficit_amount: "400",
    protein_per_lb: "0.8",
    // Body fat (optional)
    body_fat_input: "skip", // "navy" | "manual" | "skip"
    neck_inches:    "",
    waist_inches:   "",
    hip_inches:     "",
    body_fat_manual:"",
  });

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // Derived values for preview
  const heightInches = () => {
    const ft = parseFloat(form.height_ft) || 0;
    const inches = parseFloat(form.height_in) || 0;
    return ft * 12 + inches;
  };

  const weightGoalCalc = () => {
    const start = parseFloat(form.weight_start);
    if (!start) return null;
    if (form.goal_mode === "percent") {
      const pct = parseFloat(form.goal_pct) || 8;
      return Math.round(start * (1 - pct / 100));
    }
    return parseFloat(form.weight_goal) || null;
  };

  const tdeePreview = () => {
    const w = parseFloat(form.weight_start);
    const h = heightInches();
    const a = parseFloat(form.age);
    if (!w || !h || !a) return null;
    return calculateTDEE({ weight_lbs: w, height_inches: h, age: a, sex: form.sex, activity_level: form.activity_level });
  };

  const calorieTarget = () => {
    const tdee = tdeePreview();
    if (!tdee) return null;
    return Math.max(1200, tdee - parseInt(form.deficit_amount, 10));
  };

  const weeksToGoal = () => {
    const start = parseFloat(form.weight_start);
    const goal  = weightGoalCalc();
    if (!start || !goal) return null;
    return Math.ceil((start - goal) / 1);   // ~1 lb/week
  };

  // ── Validation per step ──────────────────────────────────────────────────
  const validateStep = () => {
    setError("");
    if (step === 0) {
      if (!form.name.trim())                    return "Please enter your name.";
      if (!form.age || form.age < 16 || form.age > 99) return "Please enter a valid age (16–99).";
      if (!form.height_ft || parseFloat(form.height_ft) < 3) return "Please enter your height.";
    }
    if (step === 1) {
      const w = parseFloat(form.weight_start);
      if (!w || w < 80 || w > 800) return "Please enter a valid starting weight (80–800 lbs).";
      if (form.goal_mode === "weight") {
        const g = parseFloat(form.weight_goal);
        if (!g || g >= w)           return "Goal weight must be less than starting weight.";
        if (w - g > w * 0.25)       return "Goal weight loss over 25% of body weight is too aggressive. Aim for 5–15%.";
      }
    }
    if (step === 3) {
      const bf = form.body_fat_input;
      if (bf === "navy") {
        if (!form.neck_inches || !form.waist_inches) return "Please enter neck and waist measurements.";
        if (form.sex === "female" && !form.hip_inches) return "Please enter hip measurement.";
      }
      if (bf === "manual") {
        const v = parseFloat(form.body_fat_manual);
        if (!v || v < 3 || v > 60) return "Please enter a valid body fat % (3–60).";
      }
    }
    return null;
  };

  const nextStep = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError("");
    setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  };

  const prevStep = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  // ── Final submit ─────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const err = validateStep();
    if (err) { setError(err); return; }

    setLoading(true);
    setError("");

    try {
      const w       = parseFloat(form.weight_start);
      const h       = heightInches();
      const age     = parseFloat(form.age);
      const goal    = weightGoalCalc();
      const tdee    = calculateTDEE({ weight_lbs: w, height_inches: h, age, sex: form.sex, activity_level: form.activity_level });
      const deficit = parseInt(form.deficit_amount, 10);
      const calTarget = Math.max(1200, tdee - deficit);
      const proteinPerLb = parseFloat(form.protein_per_lb);
      const macros  = calculateMacros({ calorie_target: calTarget, weight_lbs: w, protein_per_lb: proteinPerLb });

      // Body fat
      let bodyFatPct = null;
      let neck  = form.neck_inches  ? parseFloat(form.neck_inches)  : null;
      let waist = form.waist_inches ? parseFloat(form.waist_inches) : null;
      let hip   = form.hip_inches   ? parseFloat(form.hip_inches)   : null;

      if (form.body_fat_input === "navy") {
        bodyFatPct = estimateBodyFat({ sex: form.sex, neck_inches: neck, waist_inches: waist, hip_inches: hip, height_inches: h });
      } else if (form.body_fat_input === "manual") {
        bodyFatPct = parseFloat(form.body_fat_manual);
        neck = null; waist = null; hip = null;
      } else {
        neck = null; waist = null; hip = null;
      }

      const profile = {
        name:           form.name.trim(),
        sex:            form.sex,
        age:            age,
        height_inches:  h,
        weight_start:   w,
        weight_goal:    goal,
        activity_level: form.activity_level,
        tdee,
        deficit_amount: deficit,
        calorie_target: calTarget,
        protein_per_lb: proteinPerLb,
        protein_target: macros.protein_target,
        carb_pct:       macros.carb_pct,
        fat_pct:        macros.fat_pct,
        neck_inches:    neck,
        waist_inches:   waist,
        hip_inches:     hip,
        body_fat_pct:   bodyFatPct,
      };

      const { error: saveErr } = await createUserProfile(user.uid, profile);
      if (saveErr) { setError(saveErr); setLoading(false); return; }

      await refreshProfile(user.uid);
      navigate("/", { replace: true });
    } catch (e) {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  // ── Render steps ─────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      case 0: return <StepBasicInfo form={form} set={set} />;
      case 1: return <StepWeightGoal form={form} set={set} weightGoalCalc={weightGoalCalc} weeksToGoal={weeksToGoal} />;
      case 2: return <StepNutritionTargets form={form} set={set} tdeePreview={tdeePreview} calorieTarget={calorieTarget} />;
      case 3: return <StepBodyFat form={form} set={set} />;
      case 4: return <StepReview form={form} tdeePreview={tdeePreview} calorieTarget={calorieTarget} weightGoalCalc={weightGoalCalc} weeksToGoal={weeksToGoal} />;
      default: return null;
    }
  };

  const stepLabels = ["Your Info", "Your Goal", "Nutrition", "Body Fat", "Review"];
  const isLastStep = step === TOTAL_STEPS - 1;

  return (
    <div className="min-h-screen bg-[#0d1117] flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#ff7c0a]/4 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-lg animate-fade-in">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="text-[#ff7c0a] text-xs font-semibold uppercase tracking-widest">
            Step {step + 1} of {TOTAL_STEPS} — {stepLabels[step]}
          </span>
          <div className="text-2xl font-bold mt-1 text-slate-100"
               style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.05em" }}>
            Let's set up your cut
          </div>
        </div>

        <div className="card">
          <StepIndicator current={step} total={TOTAL_STEPS} />

          {renderStep()}

          {error && (
            <div className="bg-red-950/50 border border-red-800/50 text-red-400 text-sm px-4 py-3 rounded-lg mt-4">
              {error}
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3 mt-6">
            {step > 0 && (
              <button onClick={prevStep} className="btn-ghost flex-1">
                ← Back
              </button>
            )}
            {isLastStep ? (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Setting up…</>
                ) : "Start My Cut 🔥"}
              </button>
            ) : (
              <button onClick={nextStep} className="btn-primary flex-1">
                Continue →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 0 — Basic Info
// ─────────────────────────────────────────────────────────────────────────────
function StepBasicInfo({ form, set }) {
  return (
    <div className="space-y-4 animate-fade-in">
      <StepHeader icon="👤" title="About You" subtitle="This info is used to calculate your metabolic rate." />

      <div>
        <label className="label">Your Name</label>
        <input className="input" placeholder="e.g. Alex" value={form.name}
          onChange={(e) => set("name", e.target.value)} />
      </div>

      <div>
        <label className="label">Sex</label>
        <div className="grid grid-cols-2 gap-2">
          {["male", "female"].map((s) => (
            <button key={s} type="button"
              onClick={() => set("sex", s)}
              className={`py-2.5 rounded-lg text-sm font-semibold border transition-all ${
                form.sex === s
                  ? "bg-[#ff7c0a]/20 border-[#ff7c0a] text-[#ff7c0a]"
                  : "bg-[#111820] border-[#263347] text-slate-400 hover:border-slate-500"
              }`}>
              {s === "male" ? "♂ Male" : "♀ Female"}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="label">Age</label>
        <input className="input" type="number" placeholder="30" min="16" max="99"
          value={form.age} onChange={(e) => set("age", e.target.value)} />
      </div>

      <div>
        <label className="label">Height</label>
        <div className="grid grid-cols-2 gap-2">
          <div className="relative">
            <input className="input pr-8" type="number" placeholder="5" min="3" max="8"
              value={form.height_ft} onChange={(e) => set("height_ft", e.target.value)} />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">ft</span>
          </div>
          <div className="relative">
            <input className="input pr-8" type="number" placeholder="10" min="0" max="11"
              value={form.height_in} onChange={(e) => set("height_in", e.target.value)} />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">in</span>
          </div>
        </div>
      </div>

      <div>
        <label className="label">Activity Level</label>
        <div className="space-y-2">
          {ACTIVITY_OPTIONS.map((opt) => (
            <button key={opt.value} type="button"
              onClick={() => set("activity_level", opt.value)}
              className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                form.activity_level === opt.value
                  ? "bg-[#ff7c0a]/10 border-[#ff7c0a]/50 text-slate-100"
                  : "bg-[#111820] border-[#263347] text-slate-400 hover:border-slate-500"
              }`}>
              <div className="text-sm font-semibold">{opt.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 1 — Weight & Goal
// ─────────────────────────────────────────────────────────────────────────────
function StepWeightGoal({ form, set, weightGoalCalc, weeksToGoal }) {
  const goal  = weightGoalCalc();
  const weeks = weeksToGoal();

  return (
    <div className="space-y-4 animate-fade-in">
      <StepHeader icon="🎯" title="Your Goal" subtitle="Where are you starting, and where do you want to be?" />

      <div>
        <label className="label">Current Weight (lbs)</label>
        <input className="input" type="number" placeholder="185" min="80" max="800"
          value={form.weight_start} onChange={(e) => set("weight_start", e.target.value)} />
      </div>

      {/* Goal mode */}
      <div>
        <label className="label">Set Goal By</label>
        <div className="grid grid-cols-2 gap-2">
          {[["weight", "Target Weight"], ["percent", "% Body Weight Loss"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => set("goal_mode", v)}
              className={`py-2.5 rounded-lg text-sm font-semibold border transition-all ${
                form.goal_mode === v
                  ? "bg-[#ff7c0a]/20 border-[#ff7c0a] text-[#ff7c0a]"
                  : "bg-[#111820] border-[#263347] text-slate-400 hover:border-slate-500"
              }`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {form.goal_mode === "weight" ? (
        <div>
          <label className="label">Goal Weight (lbs)</label>
          <input className="input" type="number" placeholder="165" min="80" max="800"
            value={form.weight_goal} onChange={(e) => set("weight_goal", e.target.value)} />
        </div>
      ) : (
        <div>
          <label className="label">Target Loss: {form.goal_pct}% of body weight</label>
          <input type="range" min="5" max="15" step="1" value={form.goal_pct}
            onChange={(e) => set("goal_pct", e.target.value)}
            className="w-full accent-[#ff7c0a]" />
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>5% (conservative)</span><span>15% (aggressive)</span>
          </div>
        </div>
      )}

      {/* Projection preview */}
      {goal && weeks && parseFloat(form.weight_start) > 0 && (
        <div className="bg-[#111820] border border-[#263347] rounded-lg p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Projection at ~1 lb/week</div>
          <div className="flex items-end gap-4">
            <div>
              <div className="text-slate-400 text-xs">Goal weight</div>
              <div className="text-[#ff7c0a] font-bold text-xl">{goal} lbs</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs">Estimated time</div>
              <div className="text-slate-100 font-bold text-xl">{weeks} weeks</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs">To lose</div>
              <div className="text-slate-100 font-bold text-xl">{(parseFloat(form.weight_start) - goal).toFixed(0)} lbs</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 2 — Nutrition Targets
// ─────────────────────────────────────────────────────────────────────────────
function StepNutritionTargets({ form, set, tdeePreview, calorieTarget }) {
  const tdee     = tdeePreview();
  const calTarget = calorieTarget();

  return (
    <div className="space-y-4 animate-fade-in">
      <StepHeader icon="🥩" title="Nutrition Targets" subtitle="Adjust your deficit and protein. High protein preserves muscle during a cut." />

      {/* TDEE preview */}
      {tdee && (
        <div className="bg-[#111820] border border-[#263347] rounded-lg p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Your Estimated TDEE</div>
          <div className="text-[#ff7c0a] text-3xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {tdee.toLocaleString()} <span className="text-sm text-slate-400 font-normal">cal/day</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">Mifflin-St Jeor equation + activity multiplier</div>
        </div>
      )}

      {/* Deficit slider */}
      <div>
        <label className="label">Daily Deficit: {form.deficit_amount} cal/day</label>
        <input type="range" min="200" max="700" step="50" value={form.deficit_amount}
          onChange={(e) => set("deficit_amount", e.target.value)}
          className="w-full accent-[#ff7c0a]" />
        <div className="flex justify-between text-xs text-slate-500 mt-1">
          <span>200 (slow & easy)</span><span>700 (aggressive)</span>
        </div>
      </div>

      {/* Calorie target preview */}
      {calTarget && (
        <div className="flex items-center justify-between bg-[#111820] border border-[#263347] rounded-lg p-4">
          <span className="text-slate-400 text-sm">Your daily calorie target</span>
          <span className="text-[#ff7c0a] text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {calTarget.toLocaleString()} cal
          </span>
        </div>
      )}

      {calTarget === 1200 && (
        <div className="text-xs text-yellow-400 bg-yellow-900/20 border border-yellow-800/30 rounded-lg px-3 py-2">
          ⚠️ Target has been set to the minimum safe floor of 1,200 cal/day.
        </div>
      )}

      {/* Protein slider */}
      <div>
        <label className="label">Protein Target: {form.protein_per_lb}g per lb of bodyweight</label>
        <input type="range" min="0.7" max="1.0" step="0.05" value={form.protein_per_lb}
          onChange={(e) => set("protein_per_lb", e.target.value)}
          className="w-full accent-[#ff7c0a]" />
        <div className="flex justify-between text-xs text-slate-500 mt-1">
          <span>0.7g (minimum)</span><span>1.0g (optimal)</span>
        </div>
        {parseFloat(form.weight_start) > 0 && (
          <div className="text-xs text-slate-400 mt-1">
            = {Math.round(parseFloat(form.weight_start) * parseFloat(form.protein_per_lb))}g protein/day
          </div>
        )}
      </div>

      <div className="bg-[#ff7c0a]/5 border border-[#ff7c0a]/20 rounded-lg p-3">
        <p className="text-xs text-slate-400">
          💡 <strong className="text-[#ff7c0a]">Why protein matters:</strong> High protein intake (0.8–1.0g/lb)
          during a calorie deficit helps preserve lean muscle mass. This is the #1 priority on a cut.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 3 — Body Fat (optional)
// ─────────────────────────────────────────────────────────────────────────────
function StepBodyFat({ form, set }) {
  return (
    <div className="space-y-4 animate-fade-in">
      <StepHeader
        icon="📐"
        title="Body Composition"
        subtitle="Optional. Used for body fat projection charts. Completely skippable."
      />

      <div className="space-y-2">
        {[
          { v: "navy",   l: "Navy Method (measurements)",  d: "Estimate from neck/waist/hip" },
          { v: "manual", l: "Enter manually",              d: "If you already know your BF%" },
          { v: "skip",   l: "Skip for now",               d: "Can be added later in settings" },
        ].map(({ v, l, d }) => (
          <button key={v} type="button" onClick={() => set("body_fat_input", v)}
            className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
              form.body_fat_input === v
                ? "bg-[#ff7c0a]/10 border-[#ff7c0a]/50 text-slate-100"
                : "bg-[#111820] border-[#263347] text-slate-400 hover:border-slate-500"
            }`}>
            <div className="text-sm font-semibold">{l}</div>
            <div className="text-xs text-slate-500 mt-0.5">{d}</div>
          </button>
        ))}
      </div>

      {form.body_fat_input === "navy" && (
        <div className="space-y-3 border-t border-[#1f2a3c] pt-4 animate-fade-in">
          <p className="text-xs text-slate-500">Measure at widest point. Use a soft tape measure.</p>
          <div>
            <label className="label">Neck (inches)</label>
            <input className="input" type="number" placeholder="15.0" step="0.5"
              value={form.neck_inches} onChange={(e) => set("neck_inches", e.target.value)} />
          </div>
          <div>
            <label className="label">Waist (inches) — at navel</label>
            <input className="input" type="number" placeholder="34.0" step="0.5"
              value={form.waist_inches} onChange={(e) => set("waist_inches", e.target.value)} />
          </div>
          {form.sex === "female" && (
            <div>
              <label className="label">Hips (inches) — widest point</label>
              <input className="input" type="number" placeholder="40.0" step="0.5"
                value={form.hip_inches} onChange={(e) => set("hip_inches", e.target.value)} />
            </div>
          )}
        </div>
      )}

      {form.body_fat_input === "manual" && (
        <div className="animate-fade-in">
          <label className="label">Body Fat % (e.g. 22)</label>
          <input className="input" type="number" placeholder="22" min="3" max="60"
            value={form.body_fat_manual} onChange={(e) => set("body_fat_manual", e.target.value)} />
        </div>
      )}

      <p className="text-xs text-slate-600">
        All body composition data is an estimate and not medical information.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 4 — Review & Confirm
// ─────────────────────────────────────────────────────────────────────────────
function StepReview({ form, tdeePreview, calorieTarget, weightGoalCalc, weeksToGoal }) {
  const tdee     = tdeePreview();
  const calTarget = calorieTarget();
  const goal     = weightGoalCalc();
  const weeks    = weeksToGoal();
  const proteinG = Math.round(parseFloat(form.weight_start || 0) * parseFloat(form.protein_per_lb));

  const rows = [
    { label: "Name",           value: form.name },
    { label: "Sex / Age",      value: `${form.sex === "male" ? "Male" : "Female"}, ${form.age}` },
    { label: "Starting Weight", value: `${form.weight_start} lbs` },
    { label: "Goal Weight",     value: goal ? `${goal} lbs` : "—" },
    { label: "Est. Timeline",   value: weeks ? `~${weeks} weeks` : "—" },
    { label: "TDEE",            value: tdee ? `${tdee.toLocaleString()} cal` : "—" },
    { label: "Daily Deficit",   value: `${form.deficit_amount} cal` },
    { label: "Calorie Target",  value: calTarget ? `${calTarget.toLocaleString()} cal/day` : "—" },
    { label: "Protein Target",  value: `${proteinG}g/day (${form.protein_per_lb}g/lb)` },
    { label: "Activity Level",  value: ACTIVITY_OPTIONS.find((o) => o.value === form.activity_level)?.label },
  ];

  return (
    <div className="animate-fade-in">
      <StepHeader icon="✅" title="Ready to Start" subtitle="Review your plan below and hit the button to begin." />

      <div className="space-y-2">
        {rows.map(({ label, value }) => (
          <div key={label} className="flex justify-between text-sm py-2 border-b border-[#1f2a3c] last:border-0">
            <span className="text-slate-500">{label}</span>
            <span className="text-slate-100 font-medium text-right">{value}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 bg-[#ff7c0a]/5 border border-[#ff7c0a]/20 rounded-lg p-3">
        <p className="text-xs text-slate-400">
          You can adjust all of these settings later in your profile.
          The app adapts every 14 days based on your actual weight trend.
        </p>
      </div>
    </div>
  );
}
